<script setup>
import { onBeforeUnmount, onMounted, ref } from "vue";
import Vapor from "laravel-vapor";

const logos = [
    {
        src: Vapor.asset("images/brand/logo-entertainment.png"),
        alt: "Entertainment",
        class: "h-[36px]",
    },
    {
        src: Vapor.asset("images/brand/logo-fast-company.png"),
        alt: "Fast Company",
        class: "h-[27px]",
    },
    {
        src: Vapor.asset("images/brand/logo-gma.png"),
        alt: "GMA",
        class: "h-[47px]",
    },
    {
        src: Vapor.asset("images/brand/logo-ringer.png"),
        alt: "The Ringer",
        class: "h-[45px]",
    },
];

// Mobile shows one logo at a time and crossfades to the next on a timer.
// Users who prefer reduced motion get the static wrapped row at every size.
const ROTATE_INTERVAL_MS = 3000;

const activeIndex = ref(0);
const reducedMotion = ref(false);
let rotateTimer = null;

onMounted(() => {
    reducedMotion.value =
        window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ??
        false;

    if (reducedMotion.value) {
        return;
    }

    rotateTimer = setInterval(() => {
        activeIndex.value = (activeIndex.value + 1) % logos.length;
    }, ROTATE_INTERVAL_MS);
});

onBeforeUnmount(() => {
    clearInterval(rotateTimer);
});
</script>

<template>
    <section class="pb-10 pt-12">
        <div class="mx-auto max-w-[1400px]">
            <div
                class="flex flex-col items-center gap-6 xl:flex-row xl:justify-center xl:gap-12"
            >
                <p
                    class="font-brand text-[15px] font-semibold uppercase tracking-widest text-[#c480ec]"
                >
                    As Featured on:
                </p>

                <!--
                    Mobile: crossfading rotator, sized to the tallest logo so
                    the band doesn't jump between slides.
                -->
                <div
                    v-if="!reducedMotion"
                    class="relative h-[47px] w-full md:hidden"
                    data-test="logo-rotator"
                >
                    <div
                        v-for="(logo, index) in logos"
                        :key="logo.alt"
                        class="absolute inset-0 flex items-center justify-center transition-opacity duration-700 ease-in-out"
                        :class="
                            index === activeIndex ? 'opacity-100' : 'opacity-0'
                        "
                        :aria-hidden="index !== activeIndex"
                    >
                        <img
                            :src="logo.src"
                            :alt="logo.alt"
                            class="w-auto object-contain"
                            :class="logo.class"
                        />
                    </div>
                </div>

                <!--
                    md+: static centred row that wraps under the title; only
                    from xl is there room for the title beside it. Also the
                    mobile treatment when motion is reduced.
                -->
                <div
                    class="flex flex-wrap items-center justify-center gap-x-12 gap-y-6"
                    :class="reducedMotion ? '' : 'max-md:hidden'"
                    data-test="logo-row"
                >
                    <img
                        v-for="logo in logos"
                        :key="logo.alt"
                        :src="logo.src"
                        :alt="logo.alt"
                        class="w-auto object-contain"
                        :class="logo.class"
                    />
                </div>
            </div>
        </div>
    </section>
</template>
